/* eslint-disable react/no-array-index-key */
import { Box, FormLabel } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import {
  FieldWrapperVerticalV2,
  generateLocaleId,
  NumericTextField,
  useEntityPermission,
  useMasterData,
} from '@ui-framework/index';
import { observer } from 'mobx-react';
import React, { forwardRef, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Currency from '../../../Currency';

export function calculatePartsTotal(partsArray) {
  if (!Array.isArray(partsArray)) return 0;
  return partsArray.reduce((total, part) => {
    const quantity = parseFloat(part.partQuantity) || 0;
    const unitCost = parseFloat(part.partUnitCost) || 0;
    const tax = parseFloat(part.partTax) || 0;
    const handling = parseFloat(part.partHandling) || 0;
    if (quantity > 0) {
      return total + tax + handling + quantity * unitCost;
    }
    return total;
  }, 0);
}

export function sumArrayElements(arr) {
  if (!Array.isArray(arr)) return 0;
  return arr.reduce(
    (sum, val) => sum + (val != null ? Number(val) || 0 : 0),
    0,
  );
}

function Amount({ control, name, label, hide = false, permission = 'READ' }) {
  const { isHidden, isReadOnly } = useEntityPermission();
  if (hide) {
    return null;
  }
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: controllerField }) => {
        if (isReadOnly(permission, controllerField?.value)) {
          return <Currency name={name}>{controllerField?.value}</Currency>;
        }

        if (isHidden(permission)) {
          return (
            <input type="hidden" name={name} value={controllerField?.value} />
          );
        }

        return (
          <FieldWrapperVerticalV2
            field={{
              name,
              label: {
                localeId: generateLocaleId(label),
                defaultMessage: label,
              },
            }}>
            <NumericTextField
              sx={{ width: '180px' }}
              decimalScale={2}
              fixedDecimalScale
              positiveNumber
              startAdornment="$"
              isCurrency
              {...controllerField}
            />
          </FieldWrapperVerticalV2>
        );
      }}
    />
  );
}

function Column({ contents }) {
  // Filter out any falsy values (null, undefined, false, etc.)
  const filteredContents = contents.filter(Boolean);
  return (
    <Grid2 xs="grow" container direction="column" sx={{ mx: 0.5 }}>
      {filteredContents.map((c, idx) => {
        return (
          <Grid2
            key={idx}
            sx={{
              my: 1,
              display: 'flex',
              alignItems: 'center',
              height: '20px',
            }}>
            {typeof c === 'string' ? <FormLabel>{c}</FormLabel> : c}
          </Grid2>
        );
      })}
    </Grid2>
  );
}

const ReqAuthApprWidget = forwardRef((props, ref) => {
  const { control } = useFormContext();
  const amounts = props?.value || {};
  const { partsAllowed, allowedAmountTypes, claimCount } = props;
  const claimAmountTypesAllowed = allowedAmountTypes;
  if (partsAllowed && !claimAmountTypesAllowed.includes('PARTS_TOTAL')) {
    claimAmountTypesAllowed.push('PARTS_TOTAL');
  }
  const [totalPartAmount, setTotalPartAmount] = useState(0);
  const [totalRequestedAmount, settotalRequestedAmount] = useState(0);
  const [totalAmountApproved, settotalAmountApproved] = useState(
    props?.claimData?.totalAmountApproved,
  );
  const { claimAmountTypes } = useMasterData();

  const { getValues, watch } = useFormContext();

  const showPreviousClaimAmountDetails =
    [
      'INCOMPLETE',
      'PENDING_ADMINISTRATOR_REVIEW',
      'PENDING_UNDERWRITER_REVIEW',
      'PENDING_UNDERWRITER_RECEIPT',
      'CORRECTION_DRAFT',
    ].includes(props?.claimData?.claimStatus) && claimCount > 1;

  const hasClaimId = props?.claimId || false;
  const partsData = getValues('parts');
  const amountsProps = watch('amounts');

  const shouldHideApproved = () => {
    // If amountApproved is not set, hide the approved column
    if (
      !props?.permissions?.fields?.claimAmounts?.amountApproved ||
      props?.permissions?.fields?.claimAmounts?.amountApproved === 'NONE'
    ) {
      return true;
    }
    return false;
  };

  const hideApproved = shouldHideApproved();

  useEffect(() => {
    const total = calculatePartsTotal(partsData);
    setTotalPartAmount(total);
  }, [JSON.stringify(partsData)]);

  useEffect(() => {
    const total = [
      amountsProps?.LABOR?.amountRequested,
      amountsProps?.FREIGHT_AND_SHIPPING?.amountRequested,
      amountsProps?.TRAVEL?.amountRequested,
      amountsProps?.TAX?.amountRequested,
      amountsProps?.OTHER?.amountRequested,
      totalPartAmount,
    ];
    settotalRequestedAmount(sumArrayElements(total));
  }, [
    amountsProps?.LABOR?.amountRequested,
    amountsProps?.FREIGHT_AND_SHIPPING?.amountRequested,
    amountsProps?.TRAVEL?.amountRequested,
    amountsProps?.TAX?.amountRequested,
    amountsProps?.OTHER?.amountRequested,
    totalPartAmount,
  ]);

  useEffect(() => {
    const total = [
      amountsProps?.LABOR?.amountApproved,
      amountsProps?.FREIGHT_AND_SHIPPING?.amountApproved,
      amountsProps?.TRAVEL?.amountApproved,
      amountsProps?.TAX?.amountApproved,
      amountsProps?.PARTS_TOTAL?.amountApproved,
      amountsProps?.OTHER?.amountApproved,
    ];
    settotalAmountApproved(sumArrayElements(total));
  }, [
    amountsProps?.LABOR?.amountApproved,
    amountsProps?.FREIGHT_AND_SHIPPING?.amountApproved,
    amountsProps?.TRAVEL?.amountApproved,
    amountsProps?.TAX?.amountApproved,
    amountsProps?.OTHER?.amountApproved,
    amountsProps?.PARTS_TOTAL?.amountApproved,
  ]);

  const headers = ['\u00A0', 'Requested'];

  if (hasClaimId) {
    headers.push('Authorized');
  }
  if (showPreviousClaimAmountDetails) {
    headers.push('Previously Claimed');
    headers.push('Remaining');
  }
  if (!hideApproved) {
    headers.push('Approved');
  }

  return (
    <Box ref={ref}>
      <Grid2 container direction="row">
        <Column contents={headers} />
        {allowedAmountTypes.map(type => {
          const config = claimAmountTypes.find(ca => ca.value === type);
          if (!config) return null;
          return (
            <Column
              key={type}
              contents={[
                config.label,
                type === 'PARTS_TOTAL' ? (
                  <Currency>
                    {parseFloat(totalPartAmount || 0).toFixed(2)}
                  </Currency>
                ) : (
                  <Amount
                    control={control}
                    name={`amounts.${type}.amountRequested`}
                    permission={
                      props?.permissions?.fields?.claimAmounts?.amountRequested
                    }
                  />
                ),
                hasClaimId &&
                  (type === 'PARTS_TOTAL' ? (
                    <Currency>
                      {props?.claimData?.totalPartsAuthorized}
                    </Currency>
                  ) : (
                    <Currency>{amounts?.[type]?.amountAuthorized}</Currency>
                  )),
                showPreviousClaimAmountDetails &&
                  (type === 'PARTS_TOTAL' ? (
                    <Currency>
                      {props?.claimData?.previousTotalPartsApproved}
                    </Currency>
                  ) : (
                    <Currency>{amounts?.[type]?.previousApproved}</Currency>
                  )),
                showPreviousClaimAmountDetails &&
                  (type === 'PARTS_TOTAL' ? (
                    <Currency>
                      {props?.claimData?.remainingTotalPartsAuthorized}
                    </Currency>
                  ) : (
                    <Currency>{amounts?.[type]?.remainingAuthorized}</Currency>
                  )),
                !hideApproved && (
                  <Amount
                    control={control}
                    name={`amounts.${type}.amountApproved`}
                    hide={hideApproved}
                    permission={
                      props?.permissions?.fields?.claimAmounts?.amountApproved
                    }
                  />
                ),
              ]}
            />
          );
        })}
        <Column
          contents={[
            'Total',

            <Currency>
              {props?.permissions?.fields?.claimAmounts?.amountRequested ===
              'READ'
                ? props?.claimData?.totalAmountRequested
                : totalRequestedAmount}
            </Currency>,
            hasClaimId && (
              <Currency>{props?.claimData?.totalAmountAuthorized}</Currency>
            ),
            showPreviousClaimAmountDetails && (
              <Currency>
                {props?.claimData?.previousTotalAmountApproved}
              </Currency>
            ),
            showPreviousClaimAmountDetails && (
              <Currency>
                {props?.claimData?.remainingTotalAmountAuthorized}
              </Currency>
            ),
            !hideApproved && (
              <Currency>
                {props?.permissions?.fields?.claimAmounts?.amountApproved ===
                'READ'
                  ? props?.claimData?.totalAmountApproved
                  : totalAmountApproved}
              </Currency>
            ),
          ]}
        />
      </Grid2>
    </Box>
  );
});

export default observer(ReqAuthApprWidget);
